import { useEffect } from "react";
import { getCurrentValueForKey } from "@/hooks/useAdminStorage";
import type { SiteMetaData } from "@/components/feature/SiteMetaSettings";

const SITE_META_KEY = "ma_site_meta";
const DEFAULT_SITE_META: SiteMetaData = {
  siteName: "Makers Alley Co",
  description: "A permanent, algorithm-free home for independent makers.",
  domain: "https://makersalley.co",
  ogImage: "",
  favicon: "/favicon.png",
  tags: "",
  googleVerification: "",
};

const getDomain = () => {
  const siteMeta = getCurrentValueForKey<SiteMetaData>(SITE_META_KEY, DEFAULT_SITE_META);
  if (siteMeta.domain) return siteMeta.domain.replace(/\/$/, "");
  const envUrl = import.meta.env.VITE_SITE_URL as string | undefined;
  if (envUrl) return envUrl.replace(/\/$/, "");
  return "https://makersalley.co";
};

/** Read the stored OG image from ma_site_meta */
export function getStoredOgImage(): string {
  const siteMeta = getCurrentValueForKey<SiteMetaData>(SITE_META_KEY, DEFAULT_SITE_META);
  return siteMeta.ogImage || "";
}

export interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalPath?: string;
  ogType?: string;
  ogImage?: string;
  ogTitle?: string;
  ogDescription?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  noIndex?: boolean;
  noFollow?: boolean;
  lastModified?: string;
  author?: string;
  twitterImage?: string;
}

const setMeta = (selector: string, content: string | null, attr?: { name?: string; property?: string }) => {
  let el = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!el && content !== null) {
    el = document.createElement("meta");
    if (attr?.name) el.setAttribute("name", attr.name);
    if (attr?.property) el.setAttribute("property", attr.property);
    document.head.appendChild(el);
  }
  if (el && content === null) {
    el.remove();
  } else if (el && content !== null) {
    el.setAttribute("content", content);
  }
};

const setCanonical = (href: string) => {
  let el = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const setFavicon = (href: string) => {
  if (!href) return;
  const configs: { rel: string; type?: string; sizes?: string }[] = [
    { rel: "icon", type: "image/png" },
    { rel: "apple-touch-icon", sizes: "180x180" },
    { rel: "shortcut icon", type: "image/png" },
  ];
  configs.forEach((cfg) => {
    const sel = `link[rel="${cfg.rel}"]`;
    let el = document.head.querySelector(sel) as HTMLLinkElement | null;
    if (!el) {
      el = document.createElement("link");
      el.setAttribute("rel", cfg.rel);
      if (cfg.type) el.setAttribute("type", cfg.type);
      if (cfg.sizes) el.setAttribute("sizes", cfg.sizes);
      document.head.appendChild(el);
    }
    el.setAttribute("href", href);
  });
};

const setGoogleVerification = (code: string | null) => {
  let el = document.head.querySelector('meta[name="google-site-verification"]') as HTMLMetaElement | null;
  if (!code) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", "google-site-verification");
    document.head.appendChild(el);
  }
  el.setAttribute("content", code);
};

const removeExistingJsonLd = () => {
  const existing = document.head.querySelectorAll('script[type="application/ld+json"][data-seo-head]');
  existing.forEach((s) => s.remove());
};

const injectJsonLd = (data: Record<string, unknown> | Record<string, unknown>[]) => {
  removeExistingJsonLd();
  const entries = Array.isArray(data) ? data : [data];
  entries.forEach((entry) => {
    const script = document.createElement("script");
    script.setAttribute("type", "application/ld+json");
    script.setAttribute("data-seo-head", "true");
    script.textContent = JSON.stringify(entry);
    document.head.appendChild(script);
  });
};

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalPath,
  ogType = "website",
  ogImage,
  ogTitle,
  ogDescription,
  jsonLd,
  noIndex,
  noFollow,
  lastModified,
  author,
  twitterImage,
}: SEOHeadProps) {
  const domain = getDomain();

  // Read stored OG image and favicon from ma_site_meta as fallbacks
  const siteMeta = getCurrentValueForKey<SiteMetaData>(SITE_META_KEY, DEFAULT_SITE_META);
  const resolvedOgImage = ogImage !== undefined ? ogImage : (siteMeta.ogImage || "");
  const resolvedFavicon = siteMeta.favicon || "/favicon.png";
  const resolvedKeywords = keywords || siteMeta.tags || "";
  const resolvedGoogleVerification = siteMeta.googleVerification || null;

  useEffect(() => {
    const fullUrl = canonicalPath ? `${domain}${canonicalPath}` : domain;

    if (title) document.title = title;
    if (description) setMeta('meta[name="description"]', description, { name: "description" });
    if (resolvedKeywords) setMeta('meta[name="keywords"]', resolvedKeywords, { name: "keywords" });
    setCanonical(fullUrl);

    // Apply favicon from site meta — create links if they don't exist
    setFavicon(resolvedFavicon);

    // Google Search Console verification
    setGoogleVerification(resolvedGoogleVerification);

    // Robots
    const directives: string[] = [];
    if (noIndex) directives.push("noindex");
    if (noFollow) directives.push("nofollow");
    if (!noIndex && !noFollow) directives.push("index, follow");
    setMeta('meta[name="robots"]', directives.join(", "), { name: "robots" });

    if (lastModified) setMeta('meta[name="last-modified"]', lastModified, { name: "last-modified" });
    if (author) setMeta('meta[name="author"]', author, { name: "author" });

    // Open Graph
    setMeta('meta[property="og:type"]', ogType, { property: "og:type" });
    setMeta('meta[property="og:url"]', fullUrl, { property: "og:url" });
    if (ogTitle || title) setMeta('meta[property="og:title"]', ogTitle || title || "", { property: "og:title" });
    if (ogDescription || description) setMeta('meta[property="og:description"]', ogDescription || description || "", { property: "og:description" });
    // Always set og:image — use resolved value (stored or passed)
    setMeta('meta[property="og:image"]', resolvedOgImage || null, { property: "og:image" });
    setMeta('meta[property="og:site_name"]', siteMeta.siteName || "Makers Alley Co", { property: "og:site_name" });

    // Twitter Card
    setMeta('meta[name="twitter:card"]', "summary_large_image", { name: "twitter:card" });
    if (ogTitle || title) setMeta('meta[name="twitter:title"]', ogTitle || title || "", { name: "twitter:title" });
    if (ogDescription || description) setMeta('meta[name="twitter:description"]', ogDescription || description || "", { name: "twitter:description" });
    const twitterImg = twitterImage || resolvedOgImage;
    if (twitterImg) setMeta('meta[name="twitter:image"]', twitterImg, { name: "twitter:image" });

    if (jsonLd) {
      injectJsonLd(jsonLd);
    } else {
      removeExistingJsonLd();
    }

    return () => {
      removeExistingJsonLd();
    };
  }, [
    title, description, resolvedKeywords, canonicalPath, ogType, resolvedOgImage,
    ogTitle, ogDescription, jsonLd, noIndex, noFollow, lastModified,
    author, twitterImage, domain, resolvedFavicon, siteMeta.siteName,
    resolvedGoogleVerification,
  ]);

  return null;
}